const moment = require('moment-timezone');

import AppConfig from "../../config/config.js";
import UniqueIdGenerator from "./uniqueIdGenerator";
import { getServerSideFunction } from "../../core/constants/serverSideFunctionList";
import { functionDetails } from "../../core/constants/functionDescription";

export default class ExpressionEvaluator {
  /***
   *
   * @param expression  example) SUM(sbFsSNWR + kLczyRuj) * giNTEjiy
   * @param mappings    example)
   * {
   *    "sbFsSNWR": {
   *        "datasetId": "77ea8bb0-9f53-11e5-9206-e6d22ac3494e",
   *        "fieldId": "bidPrice0",
   *        "value": "Bank of America USD_CAD SPOT Level 1 Bid Price"
   *     },
   *    "kLczyRuj": {
   *        "datasetId": "77dfb640-9f53-11e5-9206-e6d22ac3494e",
   *        "fieldId": "bidPrice0",
   *        "value": "Barclays USD_CAD SPOT Level 1 Bid Price"
   *    },
   *    "giNTEjiy": {
   *        "name": "multiplier",
   *        "value" 10
   *    }
   * }
   *
   */

  static generateEvaluationFunction(expression, mappings, dashboardVariables, splitExpression = true, isForValidation = false) {
    // ["SUM", "(", "sbFsSNWR ", "+", " kLczyRuj", ")", "*", "giNTEjiy"]

    let parsedExpression = splitExpression ? expression.split(AppConfig.EXPRESSION_REGEX) : [expression];
    let fn = null;
    let fnBody = "return ";

    for (let i = 0; i < parsedExpression.length; i++) {
      let key = parsedExpression[i].trim();

      if (mappings.hasOwnProperty(key)) {
        let map = mappings[key];
        if (map.fieldId) {
          // dataset and field mapping
          fnBody += `datasets["${map.datasetId}"].${map.fieldId}`;
        } else {
          // dataset mapping
          fnBody += `"${map.datasetId}"`;
        }
      } else if (dashboardVariables.hasOwnProperty(key)) {
        // dashboard variable mapping
        if (typeof dashboardVariables[key].value === "string") {
          if (dashboardVariables[key].type === 'NUMBER') {
            fnBody += parseFloat(dashboardVariables[key].value);
          } else {
            fnBody += `'${dashboardVariables[key].value}'`;
          }
        } else {
          fnBody += dashboardVariables[key].value;
        }
      } else {
        fnBody += parsedExpression[i];
      }
    }

    try {
      /***
       *    return SUM(datasets['77ea8bb0-9f53-11e5-9206-e6d22ac3494e'].bidPrice0 +
       *    datasets['77dfb640-9f53-11e5-9206-e6d22ac3494e'].bidPrice0) * 10
       */

      // replace any datasets[id].field to number for validation
      if (isForValidation) {
        fnBody = fnBody.replace(/datasets\[.*?\].([^\s]+)/g, "1")
      }

      fn = new Function("datasets", fnBody);
    } catch (e) {
      throw Error(e.message);
    }

    return fn;
  };

  static generateSeverSideFunctionMap(exp, dashboardVariables, mappings) {
    const serverSideFunctionObjects = [];
    const mappedExp = parse(exp);
    return {
      expression: mappedExp,
      serverSideFunctionObjects: serverSideFunctionObjects
    };

    function parse(exp) {
      let mappedExpression = '';
      if (exp.type === 'BinaryExpression') {
        mappedExpression += parse(exp.left);
        mappedExpression += exp.operator;
        mappedExpression += parse(exp.right);
      } else if (exp.type === 'CallExpression') {
        if (exp.arguments.length > 0) {
          mappedExpression += exp.callee.name + '(';
          exp.arguments.forEach((arg, index) => {
            if (arg.type === 'Identifier') {
              mappedExpression += arg.name;
            } else if (arg.type === 'Literal') {
              mappedExpression += typeof arg.value === 'string' ? `'${arg.value}'` : arg.value;
            } else if (arg.type === 'CallExpression') {
              mappedExpression += parse(arg);
            } else if (arg.type === 'BinaryExpression') {
              mappedExpression += parse(arg);
            } else if (arg.type === 'UnaryExpression') {
              mappedExpression += arg.operator + parse(arg.argument);
            }
            if (exp.arguments.length - 1 !== index) {
              mappedExpression += ',';
            } else {
              mappedExpression += ')';
            }
          });
          validate(exp, mappedExpression, dashboardVariables, mappings);
        } else {
          mappedExpression += exp.callee.name + '()';
        }

        if (exp.callee.name) {
          const serverSideFunction = getServerSideFunction(exp.callee.name);
          if (serverSideFunction) {
            const obj = buildServerSideFunctionObject(exp, dashboardVariables, mappings);
            // first parameter must be datasetId
            // obj.requestId = obj.params[0];
            serverSideFunctionObjects.push(obj);
            mappedExpression = serverSideFunction.expression(obj);
          }
        }
      } else if (exp.type === 'MemberExpression') {
        mappedExpression += parse(exp.object) + '.' + exp.property.name;
      } else {
        if (exp.type === 'Identifier') {
          mappedExpression += exp.name;
          validateIdentifier(exp, dashboardVariables, mappings);
        } else if (exp.type === 'Literal') {
          mappedExpression += typeof exp.value === 'string' ? `'${exp.value}'` : exp.value;
        } else if (exp.type === 'UnaryExpression') {
          mappedExpression += exp.operator + parse(exp.argument);
        }
      }
      return mappedExpression;
    }

    function validateIdentifier(arg, dashboardVariables, mappings) {
      const value = findIdentifierValue(arg, dashboardVariables, mappings);
      if (value === undefined) {
        throw Error(`${arg.name} is undefined.`);
      }
    }

    function buildServerSideFunctionObject(exp, dashboardVariables, mappings) {
      const requestId = UniqueIdGenerator.uuid();
      const obj = {
        name: exp.callee.name,
        requestId,
        params: exp.arguments.map((arg) => {
          switch (arg.type) {
            case 'CallExpression':
            case 'BinaryExpression': {
              const parsed = mapVariables(parse(arg), dashboardVariables, mappings);
              const fn = ExpressionEvaluator.generateEvaluationFunction(parsed, {}, dashboardVariables, mappings);
              return fn();
            }

            case 'Identifier': {
              return findIdentifierValue(arg, dashboardVariables, mappings);
            }
            case 'Literal': {
              return arg.value
            }
            case 'UnaryExpression': {
              return arg.operator + parse(arg.argument);
            }
          }
        })
      };
      return obj;
    }

    function findIdentifierValue(arg, dashboardVariables, mappings) {
      for (let variableId in dashboardVariables) {
        if (dashboardVariables.hasOwnProperty(variableId)) {
          if (variableId === arg.name) {
            const { type, value, timezone } = dashboardVariables[variableId];
            if (type === 'DATETIME') {
              return `${moment.tz(value, timezone).format('YYYY-MM-DDTHH:mm:ssZ')}[${timezone}]`;
            }
            return dashboardVariables[variableId].value;
          }
        }
      }

      for (let key in mappings) {
        if (mappings.hasOwnProperty(key)) {
          if (key === arg.name) {
            if (mappings[key].fieldId) {
              // dataset id and field id mapping
              return mappings[key].datasetId + "||" + mappings[key].fieldId;
            } else {
              // dataset id mapping
              return mappings[key].datasetId;
            }
          }
        }
      }

      if (window[arg.name] != null) {
        return window[arg.name];
      } else {
        throw Error(`${arg.name} is undefined.`);
      }
    }

    function mapVariables(expression, dashboardVariables, mappings) {
      const parsed = expression.split(AppConfig.EXPRESSION_REGEX);
      let fn = [];

      for (let i = 0; i < parsed.length; i++) {
        let content = parsed[i].trim();
        let value = parsed[i];
        for (let variableId in dashboardVariables) {
          if (dashboardVariables[variableId].name === content) {
            value = parsed[i].replace(dashboardVariables[variableId].name, variableId);
            break;
          }
        }
        fn.push(value);
      }

      return fn.join("");
    }

    function validate(exp, mappedExp, dashboardVariables, mappings) {
      const functionName = exp.callee.name;
      const desc = functionDetails.find(fd => fd.name.toLowerCase() === functionName.toLowerCase());
      const args = exp.arguments.map((arg) => {
        switch (arg.type) {
          case 'CallExpression':
          case 'BinaryExpression': {
            const parsed = mapVariables(parse(arg), dashboardVariables, mappings);
            const fn = ExpressionEvaluator.generateEvaluationFunction(parsed, mappings, dashboardVariables, true, true);
            return fn();
          }

          case 'Identifier': {
            return findIdentifierValue(arg, dashboardVariables, mappings);
          }
          case 'Literal': {
            return arg.value
          }
          case 'UnaryExpression': {
            return arg.operator + parse(arg.argument);
          }
        }
      });

      if (desc) {
        const numberOfArgsRequired = desc.params.filter(p => !p.optional).length;
        if (numberOfArgsRequired > args.length) {
          throw Error(generateWrongNumberArgMessage(functionName, numberOfArgsRequired, args.length));
        }
        args.forEach((arg, index) => {
          let originalArg = exp.arguments[index].value || exp.arguments[index].name;
          const param = desc.params[index] || desc.params[desc.params.length - 1];

          // if value is a reference key for mappings, argument type cannot be validated
          // because value is not availble at this time.
          if (mappings.hasOwnProperty(originalArg)) {
            return;
          }

          if (exp.arguments[index].type === 'Identifier') {
            const variable = dashboardVariables[originalArg];
            if (variable) {
              originalArg = variable.name;
            }
          }

          switch (param.type) {
            case 'date': {
              let dateValue = arg;
              if (typeof dateValue === 'string') {
                // remove timezone string (2017-08-07T08:00:01-04:00[America/New_York])
                dateValue = dateValue.substring(0, 19);
              }

              const date = new Date(dateValue);
              if (isNaN(date.getTime())) {
                throw Error(generateErrorMessage(functionName, index + 1, param.type, originalArg, typeof arg));
              }
              break;
            }
            case 'number': {
              const isNumeric = !isNaN(parseFloat(arg)) && isFinite(arg);
              if (!isNumeric) {
                throw Error(generateErrorMessage(functionName, index + 1, param.type, originalArg, typeof arg));
              }
            }
          }
        })
      } else {
        console.log('cannot find function description for ' + functionName);
      }
    }

    function generateErrorMessage(fnName, parameterIndex, type, value, valueType) {
      return `Function ${fnName} parameter ${parameterIndex} expects ${type} values. But '${value}' is a ${valueType} and cannot be coerced to a ${type}.`;
    }

    function generateWrongNumberArgMessage(fnName, numberOfArgsExpected, numberOfArgsActual) {
      return `Wrong number of arguments to ${fnName}. Expected ${numberOfArgsExpected} arguments, but got ${numberOfArgsActual} arguments.`
    }
  }
}



// WEBPACK FOOTER //
// ./src/core/utils/expressionEvaluator.js